import cloneDeep from 'lodash/cloneDeep'
import { getSavedState, saveState } from '@state/helpers'
import { findOnboardingActivity, isOnboardingActivitySubmittedForProgress } from '@utils/onboarding-helpers'

export const ONBOARDING_SCENARIO_STORAGE_KEY = 'mottocenter.onboarding.stub.scenario'
export const ONBOARDING_SCENARIO_DELAYS = Object.freeze({
  load: 650,
  submit: 900,
  signingUrl: 500,
  completeEnvelope: 700,
})

const nowIso = new Date().toISOString().slice(0, 10)

const sampleResource = {
  id: 920,
  title: 'Office Onboarding Guide',
  postType: 'media',
  isDownloadable: true,
}

const sampleLink = {
  url: 'https://mottomortgage.com/franchise/process/',
}

const firstStepsActivities = [
  {
    id: 491001,
    activity: 'Schedule and Complete First Steps Call w/OC',
    order: 4,
    description:
      'After the conclusion of the First Steps meeting, reach out to the broker owner with the following. Additionally seek approval/guidance on DBA and location.\n1. Detailed summary of the discussion\n2. Motto Email setup information\n3. Licensing Consultant introduction\n4. DBA Name Guidelines\n5. Motto 101 Information/Registration',
    posts: [sampleResource],
    urls: [sampleLink],
  },
  {
    id: 491002,
    activity: 'Register for Owner 101 Training',
    order: 3,
    description: 'Register the broker owner for the next available Motto Owner 101 training session.',
    urls: [
      {
        url: 'https://mottomortgage.com/',
      },
    ],
  },
  {
    id: 491003,
    activity: 'Create Business Entity (if applicable)',
    order: 2,
    description: 'Confirm business entity requirements and collect the documents needed for HQ review.',
  },
  {
    id: 491004,
    activity: 'Send Relevant Entity Documents to OC',
    order: 1,
    description: 'Send entity documents to the onboarding consultant and include any open questions.',
  },
]

function activity(overrides) {
  return {
    status: 'pending',
    posts: [],
    urls: [],
    ...overrides,
  }
}

function step(overrides) {
  return {
    status: 'in_progress',
    activities: [],
    ...overrides,
  }
}

function essentialChecklist(steps) {
  return {
    id: 'essential',
    label: 'Essential Steps',
    steps,
  }
}

function stateChecklist(state, steps) {
  return {
    id: `state-${state}`,
    label: state,
    state,
    steps,
  }
}

function response({ welcomeVariant = 'first', essentialSteps = [], stateChecklists = [] }) {
  const categories = []

  if (essentialSteps.length) {
    categories.push({
      id: 'essential',
      label: 'Essential Steps',
      type: 'essential',
      icon: 'flag-checkered',
      order: 1,
      checklists: [essentialChecklist(essentialSteps)],
    })
  }

  categories.push({
    id: 'state-licenses',
    label: 'State Licenses',
    type: 'state-license',
    icon: 'id-card',
    order: 2,
    checklists: stateChecklists,
  })

  return {
    hasActiveBrokerActivity: true,
    welcomeVariant,
    onboarding: {
      welcomeVariant,
      categories,
    },
  }
}

function firstEssentialStep() {
  return step({
    id: 'essential-step-1',
    title: 'Step 1: First Steps',
    order: 1,
    activities: firstStepsActivities.map(activity),
  })
}

function firstEssentialStepResponse({ welcomeVariant = 'first' } = {}) {
  return response({
    welcomeVariant,
    essentialSteps: [firstEssentialStep()],
  })
}

function licenseStep(state, idOffset = 0) {
  return step({
    id: `${state}-license-step-1`,
    title: `${state} License Checklist`,
    order: 1,
    activities: [
      activity({
        id: 491100 + idOffset,
        activity: `${state} application requirements`,
        order: 2,
        description: `Review the ${state} application requirements and gather documents for the licensing consultant.`,
      }),
      activity({
        id: 491101 + idOffset,
        activity: `${state} NMLS checklist`,
        order: 1,
        description: `Complete the ${state} NMLS checklist items assigned to the broker owner.`,
        posts: [sampleResource],
      }),
    ],
  })
}

function orderedEssentialStep() {
  return step({
    id: 'ordered-essential-step-1',
    title: 'Step 1: Ordered First Steps',
    order: 1,
    isCompletionOrdered: true,
    activities: [
      activity({
        id: 492001,
        activity: 'Collect ownership roster',
        order: 4,
        description: 'Collect ownership roster details before continuing.',
      }),
      activity({
        id: 492002,
        activity: 'Confirm entity details',
        order: 3,
        description: 'Confirm the legal entity structure before submitting.',
      }),
      activity({
        id: 492003,
        activity: 'Upload supporting documents',
        order: 2,
        description: 'Upload supporting documents after entity details are complete.',
      }),
      activity({
        id: 492004,
        activity: 'Submit opening checklist',
        order: 1,
        description: 'Submit the opening checklist after the prior activities are complete.',
      }),
    ],
  })
}

function orderedLicenseStep(state, idOffset = 0) {
  return step({
    id: `${state}-ordered-license-step-1`,
    title: `${state} Ordered License Checklist`,
    order: 1,
    activities: [
      activity({
        id: 492100 + idOffset,
        activity: `${state} ordered application requirements`,
        order: 2,
        isCompletionOrdered: true,
        description: `Review the ${state} ordered application requirements before continuing.`,
      }),
      activity({
        id: 492101 + idOffset,
        activity: `${state} ordered NMLS checklist`,
        order: 1,
        isCompletionOrdered: true,
        description: `Complete the ${state} ordered NMLS checklist after the application requirements.`,
      }),
    ],
  })
}

function progressEssentialSteps() {
  return [
    step({
      id: 'progress-essential-step-1',
      title: 'Step 1: Progress Basics',
      order: 1,
      activities: [
        activity({
          id: 493001,
          activity: 'Submit office profile',
          status: 'pending_verification',
          submissionStatus: 'pending_verification',
          submittedAt: nowIso,
          order: 2,
          description: 'Send the office profile for onboarding review.',
        }),
        activity({
          id: 493002,
          activity: 'Confirm launch contacts',
          order: 1,
          description: 'Confirm the launch contact list for the office.',
        }),
      ],
    }),
    step({
      id: 'progress-essential-step-2',
      title: 'Step 2: Launch Setup',
      order: 2,
      status: 'pending',
      availableAt: null,
      activities: [
        activity({
          id: 493003,
          activity: 'Review launch plan',
          order: 1,
          description: 'Review the launch plan after Step 1 is submitted.',
        }),
      ],
    }),
  ]
}

function progressLicenseSteps(state, idOffset = 0) {
  return [
    step({
      id: `${state}-progress-license-step-1`,
      title: `${state} Progress License Step 1`,
      order: 1,
      activities: [
        activity({
          id: 493100 + idOffset,
          activity: `${state} progress application`,
          order: 2,
          description: `Submit the ${state} progress application.`,
        }),
        activity({
          id: 493101 + idOffset,
          activity: `${state} progress NMLS items`,
          order: 1,
          description: `Submit the ${state} NMLS progress items.`,
        }),
      ],
    }),
    step({
      id: `${state}-progress-license-step-2`,
      title: `${state} Progress License Step 2`,
      order: 2,
      status: 'pending',
      availableAt: null,
      activities: [
        activity({
          id: 493102 + idOffset,
          activity: `${state} progress final review`,
          order: 1,
          description: `Review the ${state} final progress checklist.`,
        }),
      ],
    }),
  ]
}

function rejectedProgressEssentialSteps() {
  const [firstStep, secondStep] = progressEssentialSteps()

  return [
    step({
      ...firstStep,
      status: 'in_progress',
      activities: [
        activity({
          ...firstStep.activities[0],
          status: 'rejected',
          submissionStatus: 'rejected',
          submittedAt: nowIso,
          notes: 'Office profile needs one more attachment.',
        }),
        activity({
          ...firstStep.activities[1],
          status: 'pending_verification',
          submissionStatus: 'pending_verification',
          submittedAt: nowIso,
        }),
      ],
    }),
    secondStep,
  ]
}

function rejectedOverlayEssentialSteps() {
  const [firstStep, secondStep] = rejectedProgressEssentialSteps()

  return [
    step({
      ...firstStep,
      activities: [
        firstStep.activities[0],
        activity({
          ...firstStep.activities[1],
          status: 'rejected',
          submissionStatus: 'rejected',
          submittedAt: nowIso,
          notes: 'Launch contacts need a corrected phone number.',
        }),
      ],
    }),
    secondStep,
  ]
}

const scenarioDefinitions = [
  {
    id: 'first-essential-step',
    label: 'First essential step',
    description: 'Four open essential activities with no state license steps yet.',
    build: () => firstEssentialStepResponse(),
  },
  {
    id: 'ordered-essential-activities',
    label: 'Ordered essential activities',
    description: 'A step-level ordered activity list that must be submitted from top to bottom.',
    build: () =>
      response({
        essentialSteps: [orderedEssentialStep()],
      }),
  },
  {
    id: 'progress-essential-checklist',
    label: 'Essential checklist progress',
    description: 'Two essential steps where progress advances only after the first step is fully submitted.',
    build: () =>
      response({
        essentialSteps: progressEssentialSteps(),
      }),
  },
  {
    id: 'progress-state-checklists',
    label: 'State checklist progress',
    description: 'Two state checklists with independent progress.',
    build: () =>
      response({
        stateChecklists: [
          stateChecklist('CO', progressLicenseSteps('CO', 10)),
          stateChecklist('AL', progressLicenseSteps('AL', 20)),
        ],
      }),
  },
  {
    id: 'rejected-submitted-progress',
    label: 'Rejected submitted progress',
    description: 'A fully submitted step keeps progress after one activity is rejected.',
    build: () =>
      response({
        essentialSteps: rejectedProgressEssentialSteps(),
      }),
  },
  {
    id: 'independent-ordered-state-activities',
    label: 'Independent ordered state activities',
    description: 'Two state checklists with independently ordered activities.',
    build: () =>
      response({
        stateChecklists: [
          stateChecklist('CO', [orderedLicenseStep('CO', 10)]),
          stateChecklist('AL', [orderedLicenseStep('AL', 20)]),
        ],
      }),
  },
  {
    id: 'pending_verification',
    label: 'One step pending review',
    description: 'All activities in the active essential step have been submitted and are waiting on HQ approval.',
    build: () =>
      response({
        essentialSteps: [
          step({
            ...firstEssentialStep(),
            status: 'in_progress',
            activities: firstStepsActivities.map((item) =>
              activity({
                ...item,
                status: 'pending_verification',
                submissionStatus: 'pending_verification',
                submittedAt: nowIso,
              })
            ),
          }),
        ],
        stateChecklists: [stateChecklist('CO', [licenseStep('CO', 30)])],
      }),
  },
  {
    id: 'warnings',
    label: 'Second activity needs attention',
    description: 'The second activity in Step 1 shows a warning while later activities remain available.',
    build: () =>
      response({
        essentialSteps: [
          step({
            id: 'essential-step-1',
            title: 'Step 1: First Steps',
            order: 2,
            activities: [
              activity({
                ...firstStepsActivities[0],
                status: 'completed',
                dateCompleted: nowIso,
              }),
              activity({
                ...firstStepsActivities[1],
                status: 'rejected',
                submissionStatus: 'rejected',
                submittedAt: nowIso,
                notes: 'Training date is missing. Confirm the session before submitting.',
              }),
              activity(firstStepsActivities[2]),
            ],
          }),
          step({
            id: 'essential-step-2',
            title: 'Step 2: Business Setup',
            order: 1,
            status: 'pending',
            availableAt: null,
            activities: [
              activity({
                id: 491030,
                activity: 'Review business setup package',
                description: 'This step unlocks after HQ approves Step 1.',
              }),
            ],
          }),
        ],
      }),
  },
  {
    id: 'docusign',
    label: 'DocuSign required',
    description: 'An activity requires a signed document before it can be submitted.',
    build: () =>
      response({
        essentialSteps: [
          step({
            id: 'essential-step-docusign',
            title: 'Step 1: Signature Packet',
            order: 1,
            activities: [
              activity({
                id: 491040,
                activity: 'Sign Office Onboarding Agreement',
                order: 1,
                description: 'Generate and sign the office onboarding agreement before submitting the activity.',
                requiresDocusign: true,
                docusignTemplateId: 'template-4910',
                docusignEnvelopeStatus: 'sent',
              }),
            ],
          }),
        ],
      }),
  },
  {
    id: 'essential-pending-state-open',
    label: 'Essential review pending, states available',
    description: 'Essential Step 1 is pending HQ review while CO and TX state license checklists remain selectable.',
    build: () =>
      response({
        essentialSteps: [
          step({
            ...firstEssentialStep(),
            status: 'in_progress',
            activities: firstStepsActivities.map((item) =>
              activity({
                ...item,
                status: 'pending_verification',
                submissionStatus: 'pending_verification',
                submittedAt: nowIso,
              })
            ),
          }),
        ],
        stateChecklists: [stateChecklist('CO', [licenseStep('CO', 40)]), stateChecklist('TX', [licenseStep('TX', 50)])],
      }),
  },
  {
    id: 'multiple-state-checklists',
    label: 'Multiple state checklists',
    description: 'Three pursued states with independent checklist progress.',
    build: () =>
      response({
        essentialSteps: [firstEssentialStep()],
        stateChecklists: [
          stateChecklist('CO', [licenseStep('CO', 60)]),
          stateChecklist('AL', [licenseStep('AL', 70)]),
          stateChecklist('TX', [
            step({
              ...licenseStep('TX', 80),
              activities: [
                activity({
                  ...licenseStep('TX', 80).activities[0],
                  status: 'completed',
                  dateCompleted: nowIso,
                }),
                activity(licenseStep('TX', 80).activities[1]),
              ],
            }),
          ]),
        ],
      }),
  },
  {
    id: 'onboarding-welcome-overlay',
    label: 'Onboarding welcome overlay',
    description: 'First-time welcome overlay animation over the first essential step.',
    showsWelcome: true,
    build: () => firstEssentialStepResponse(),
  },
  {
    id: 'welcome-back-overlay',
    label: 'Welcome back overlay',
    description: 'Returning welcome overlay animation over the first essential step.',
    showsWelcome: true,
    build: () => firstEssentialStepResponse({ welcomeVariant: 'returning' }),
  },
  {
    id: 'rejected-submission-overlay',
    label: 'Rejected submission overlay',
    description: 'Rejected submission overlay animation over the activity with an issue.',
    showsWelcome: true,
    build: () =>
      response({
        welcomeVariant: 'rejected',
        essentialSteps: rejectedOverlayEssentialSteps(),
      }),
  },
]

const scenarioStateById = {}

function getScenarioDefinition(id) {
  return scenarioDefinitions.find((scenario) => scenario.id === id) || scenarioDefinitions[0]
}

function getScenarioState(scenarioId) {
  return scenarioStateById[scenarioId] || null
}

function setScenarioState(scenarioId, state) {
  scenarioStateById[scenarioId] = state
}

function updateStepStatus(currentStep) {
  const allActivitiesSubmitted = (currentStep.activities || []).every(isOnboardingActivitySubmittedForProgress)

  if (allActivitiesSubmitted && currentStep.status !== 'completed') {
    currentStep.status = 'in_progress'
  }
}

export const onboardingScenarioOptions = scenarioDefinitions.map(({ id, label, description, showsWelcome }) => ({
  id,
  label,
  description,
  showsWelcome: Boolean(showsWelcome),
}))

export function getSelectedOnboardingScenarioId() {
  return getSavedState(ONBOARDING_SCENARIO_STORAGE_KEY, scenarioDefinitions[0].id)
}

export function setSelectedOnboardingScenario(id) {
  saveState(ONBOARDING_SCENARIO_STORAGE_KEY, getScenarioDefinition(id).id)
}

export function resetSelectedOnboardingScenario(scenarioId = getSelectedOnboardingScenarioId()) {
  delete scenarioStateById[scenarioId]
}

export function getOnboardingScenarioResponse(scenarioId = getSelectedOnboardingScenarioId()) {
  const scenarioState = getScenarioState(scenarioId)
  if (scenarioState) {
    return cloneDeep(scenarioState)
  }

  const scenario = getScenarioDefinition(scenarioId)
  const state = scenario.build()
  setScenarioState(scenarioId, state)

  return cloneDeep(state)
}

export function waitForOnboardingScenarioDelay(type) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ONBOARDING_SCENARIO_DELAYS[type] || 0)
  })
}

export function submitOnboardingScenarioActivity(activityId, scenarioId = getSelectedOnboardingScenarioId()) {
  const state = getScenarioState(scenarioId) || getOnboardingScenarioResponse(scenarioId)
  const match = findOnboardingActivity(state, activityId)

  if (!match) {
    return {
      submitted: false,
      message: 'Activity not found.',
    }
  }

  if (match.activity.requiresDocusign && match.activity.docusignEnvelopeStatus !== 'completed') {
    return {
      submitted: false,
      requiresDocusign: true,
      message: 'Document must be signed before this activity can be submitted.',
    }
  }

  match.activity.status = 'pending_verification'
  match.activity.submissionStatus = 'pending_verification'
  match.activity.submittedAt = nowIso
  updateStepStatus(match.step)
  setScenarioState(scenarioId, state)

  return {
    submitted: true,
    activity: cloneDeep(match.activity),
    step: cloneDeep(match.step),
  }
}

export function completeOnboardingScenarioEnvelope(activityId, scenarioId = getSelectedOnboardingScenarioId()) {
  const state = getScenarioState(scenarioId) || getOnboardingScenarioResponse(scenarioId)
  const match = findOnboardingActivity(state, activityId)

  if (match) {
    match.activity.docusignEnvelopeStatus = 'completed'
    match.activity.docusignEnvelopeId = match.activity.docusignEnvelopeId || `stub-envelope-${activityId}`
    match.activity.docusignDownloadable = true
    match.activity.docusignFilename = match.activity.docusignFilename || `${match.activity.activity}.pdf`
    setScenarioState(scenarioId, state)
  }

  return {
    completed: Boolean(match),
  }
}

export function getOnboardingScenarioSigningUrl(activityId, scenarioId = getSelectedOnboardingScenarioId()) {
  completeOnboardingScenarioEnvelope(activityId, scenarioId)

  return {
    data: {
      envelopeStatus: 'sent',
      url: 'about:blank',
      simulateSigningComplete: true,
    },
  }
}
